import React, { useState, useEffect } from "react";
import axios from "axios";
import "../../wallet.css";
import { Pill } from "./walletShared";

// Continuous Scanning keeps only a short rolling log — best-effort, session-only
// (no backend "recent scans" endpoint exists yet to persist/fetch this from).
const RECENT_SCANS_KEY = "inscan_recent_scans";
// Bulk scanning produces many scans per session — keep them all (was 8, which truncated the list).
const MAX_RECENT_SCANS = 1000;

const loadRecentScans = () => {
  try {
    const raw = sessionStorage.getItem(RECENT_SCANS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (err) {
    return [];
  }
};

const saveRecentScans = (list) => {
  try {
    sessionStorage.setItem(RECENT_SCANS_KEY, JSON.stringify(list.slice(0, MAX_RECENT_SCANS)));
  } catch (err) {
    // sessionStorage unavailable/full — the log is best-effort only.
  }
};

const pad2 = (n) => String(n).padStart(2, "0");
const formatDateInput = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const formatTimeInput = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
const formatClock = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;

const BarcodeScanner = ({ onScanSuccess, projectId }) => {
  const API_URL = process.env.REACT_APP_API_URL;
  const [barcode, setBarcode] = useState("");
  const [shipmentDetails, setShipmentDetails] = useState([]);
  const [loading, setLoading] = useState(false);

  // Continuous Scanning: only Homebase is required — date/time are captured
  // automatically per scan, each scan submits immediately, and a short
  // recent-scans log (session-only) shows the result.
  const [continuousMode, setContinuousMode] = useState(false);
  const [recentScans, setRecentScans] = useState(loadRecentScans);

  // Server-backed FULL list of everything scanned today (persistent, all users) — not just this
  // session's rolling log. Backed by GET /jobs/inScanToday.
  const [todayData, setTodayData] = useState(null);
  const [todayLoading, setTodayLoading] = useState(false);
  const [todaySearch, setTodaySearch] = useState("");

  // Service-Time: a live clock, the (editable) cutoff, and the station-mode override.
  // serviceMode: AUTO (classify by cutoff) | SAME_DAY | NEXT_DAY. Resets to AUTO on refresh.
  const [now, setNow] = useState(() => new Date());
  const [cutoff, setCutoff] = useState({ cutoffTime: "09:00", timezone: "Asia/Dubai" });
  const [serviceMode, setServiceMode] = useState("AUTO");
  const [cutoffEditing, setCutoffEditing] = useState(false);
  const [cutoffDraft, setCutoffDraft] = useState("09:00");
  const [cutoffSaving, setCutoffSaving] = useState(false);

  // Result of the best-effort Project link on a batch submit (incl. partner-mismatch skips).
  const [projectMsg, setProjectMsg] = useState("");

  const fetchTodayScans = async () => {
    setTodayLoading(true);
    try {
      const res = await axios.get(`${API_URL}shypvdriverapp/jobs/inScanToday`);
      setTodayData((res && res.data && res.data.data) || null);
    } catch (e) {
      // non-fatal — the full list just stays as-is
    } finally {
      setTodayLoading(false);
    }
  };
  useEffect(() => { fetchTodayScans(); /* eslint-disable-next-line */ }, []);

  const todayAwbsFiltered = React.useMemo(() => {
    const list = (todayData && todayData.awbs) || [];
    const q = todaySearch.trim().toLowerCase();
    return q ? list.filter((a) => String(a).toLowerCase().includes(q)) : list;
  }, [todayData, todaySearch]);

  const upsertRecentScan = (entry) => {
    setRecentScans((prev) => {
      const idx = prev.findIndex((e) => e.id === entry.id);
      const next = idx >= 0 ? prev.map((e) => (e.id === entry.id ? { ...e, ...entry } : e)) : [entry, ...prev];
      const capped = next.slice(0, MAX_RECENT_SCANS);
      saveRecentScans(capped);
      return capped;
    });
  };

  const handleToggleContinuous = () => {
    setContinuousMode((prev) => !prev);
    setBarcode("");
    setShipmentDetails([]);
    setMsg("");
    setError("");
  };

  // --- Service-Time: live clock + effective cutoff -------------------------------------
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    // Global effective cutoff for the live banner. Falls back to 09:00 so scanning never blocks.
    axios
      .get(`${API_URL}shypvdriverapp/inscan-cutoff/effective`)
      .then((res) => {
        const data = res.data && res.data.data;
        if (data && data.cutoffTime) {
          setCutoff({ id: data.id, cutoffTime: data.cutoffTime, timezone: data.timezone || "Asia/Dubai" });
          setCutoffDraft(data.cutoffTime);
        }
      })
      .catch(() => {
        /* keep the 09:00 Asia/Dubai default */
      });
  }, [API_URL]);

  const cutoffTz = (cutoff && cutoff.timezone) || "Asia/Dubai";
  const cutoffHHmm = (cutoff && cutoff.cutoffTime) || "09:00";

  // Wall-clock in the cutoff's timezone (Asia/Dubai by default), 24h.
  const clockInZone = (d) =>
    new Intl.DateTimeFormat("en-GB", {
      timeZone: cutoffTz, hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
    }).format(d);

  const minutesOfDayInZone = (d) => {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: cutoffTz, hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).formatToParts(d);
    const h = parseInt(parts.find((p) => p.type === "hour").value, 10) % 24;
    const m = parseInt(parts.find((p) => p.type === "minute").value, 10);
    return h * 60 + m;
  };

  const cutoffMinutes = () => {
    const [h, m] = String(cutoffHHmm).split(":");
    return (parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0);
  };

  // After the cutoff -> Same Day; at/before -> Next Day (matches the backend rule).
  const computeAutoLabel = (d) => (minutesOfDayInZone(d) > cutoffMinutes() ? "Same Day" : "Next Day");
  const labelForMode = (mode) => (mode === "SAME_DAY" ? "Same Day" : mode === "NEXT_DAY" ? "Next Day" : null);
  const currentServiceLabel = () => (serviceMode === "AUTO" ? computeAutoLabel(new Date()) : labelForMode(serviceMode));

  const saveCutoff = () => {
    setCutoffSaving(true);
    axios
      .post(`${API_URL}shypvdriverapp/inscan-cutoff`, { partnerId: null, cutoffTime: cutoffDraft })
      .then((res) => {
        const data = res.data && res.data.data;
        setCutoff(
          data && data.cutoffTime
            ? { id: data.id, cutoffTime: data.cutoffTime, timezone: data.timezone || cutoffTz }
            : { ...cutoff, cutoffTime: cutoffDraft }
        );
        setCutoffEditing(false);
      })
      .catch((e) => setError("Failed to update cutoff: " + (e?.response?.data?.message || e.message)))
      .finally(() => setCutoffSaving(false));
  };

  // Handle the barcode scan (onChange or onKeyUp event)
  const handleBarcodeInputChange = (event) => {
    setBarcode(event.target.value);
  };

  const handleBarcodeKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault(); // Prevents form submission or unwanted behavior
      const scannedValue = barcode.trim();

      if (scannedValue) {
        if (continuousMode) {
          processContinuousBarcode(scannedValue);
        } else {
          processBarcode(scannedValue);
        }
        setTimeout(() => setBarcode(""), 100); // Allow time for barcode to register
      }
    }
  };

  const parseBarcodeValue = (scannedValue) =>
    scannedValue
      .split(/[\n,]+/)
      .map((value) => value.trim()) // ✅ Trim each value first
      .filter((value) => value !== "") // ✅ Remove any empty strings
      .map((value) => {
        if (value.startsWith("EKX")) {
          // Remove first 3 characters and last 5 characters
          return value.slice(3, -5);
        }
        return value;
      });

  const processBarcode = (scannedValue) => {
    const scannedBarcodes = parseBarcodeValue(scannedValue);
    setShipmentDetails((prevDetails) => [...prevDetails, ...scannedBarcodes]);
  };

  // Continuous Scanning: submit each scanned AWB immediately instead of
  // batching into shipmentDetails, using the current date/time automatically.
  // Process a (possibly large) pasted/scanned list in SEQUENTIAL batches of BATCH_SIZE, so a bulk
  // upload of 100+ AWBs never fires all requests at once (which exhausted the DB connection pool).
  // Each batch's AWBs run concurrently (small), and we wait for the batch to finish before the next.
  // A failed AWB resolves like any other, so one failure never stops the rest.
  const BATCH_SIZE = 12;
  const [batchProgress, setBatchProgress] = useState(null); // {done, total} while a bulk run is active

  const processContinuousBarcode = async (scannedValue) => {
    if (!scaninfo.home_base) {
      setError("Select a homebase first");
      return;
    }
    setError("");
    const awbs = parseBarcodeValue(scannedValue);
    if (awbs.length === 0) return;
    setBatchProgress({ done: 0, total: awbs.length });
    for (let i = 0; i < awbs.length; i += BATCH_SIZE) {
      const batch = awbs.slice(i, i + BATCH_SIZE);
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(batch.map((awb) => submitContinuousAwb(awb)));
      setBatchProgress({ done: Math.min(i + BATCH_SIZE, awbs.length), total: awbs.length });
    }
    setBatchProgress(null);
    fetchTodayScans();
  };

  // Returns a Promise that resolves when this AWB's scan (submit + status poll) finishes — success,
  // error or timeout — so the batch loop above can wait for it without one failure blocking the rest.
  const submitContinuousAwb = (awb) => new Promise((resolve) => {
    const now = new Date();
    const scanId = `${now.getTime()}-${awb}`;

    const serviceLabel = currentServiceLabel();
    upsertRecentScan({ id: scanId, awb, time: formatClock(now), status: "pending", message: "Processing…", serviceTime: serviceLabel });

    const payload = {
      scan_date: formatDateInput(now),
      driverId: scaninfo.driver,
      scan_time: formatTimeInput(now),
      home_base: scaninfo.home_base,
      status: "inscan",
      updated_by: sessionStorage.getItem("userid"),
      awbs: [awb],
      service_mode: serviceMode,
    };

    axios
      .post(`${API_URL}shypvdriverapp/jobs/inScanShipmentsNew`, payload)
      .then((response) => {
        if (!response.data.data) {
          upsertRecentScan({ id: scanId, status: "error", message: "No job started" });
          resolve();
          return;
        }
        const jobId = response.data.data.requestId;

        // Optional: link the scanned AWB to a finance Project.
        // Best-effort — never block or fail the in-scan itself. The backend only links when the
        // order's partner matches the project's partner; otherwise it's skipped (partner mismatch).
        if (projectId) {
          axios
            .post(`${API_URL}shypvdriverapp/projects/${projectId}/assign`, { awbs: [awb] })
            .then((r) => {
              const res = r.data && r.data.data;
              if (res && res.partnerMismatch && res.partnerMismatch.length > 0) {
                upsertRecentScan({ id: scanId, projectNote: "mismatch" });
              } else if (res && res.matched > 0) {
                upsertRecentScan({ id: scanId, projectNote: "linked" });
              }
            })
            .catch((e) => console.warn("Project link failed (in-scan still ok):", e?.message));
        }

        const maxAttempts = 30;
        const delayMs = 4000;

        const pollJobStatus = async (attempt = 1) => {
          try {
            const statusResponse = await axios.get(`${API_URL}shypvdriverapp/jobs/inScanStatus/${jobId}`);
            // The real result status lives at data.data.status (ACCEPTED while the async is still
            // processing → OK / INTERNAL_SERVER_ERROR / BAD_REQUEST when done). The old code read
            // data.STATUS (undefined) → terminated on the FIRST poll before the async finished, and
            // then only looked for an error string → a rejected order was silently shown as "Success".
            const data = (statusResponse.data && statusResponse.data.data) || {};
            const status = data.status;
            const stillProcessing = status == null || status === "ACCEPTED" || status === "PENDING" || status === "QUEUED";

            if (!stillProcessing) {
              const errors = data.errors || [];
              // Match this AWB in the error list (null-safe; errorDetails carries the business AWB).
              const errorDetail = errors.find((e) => {
                const d = String((e && (e.errorDetails || e.errorMessage)) || "");
                return d.includes(awb);
              });
              const overallFailed = status === "INTERNAL_SERVER_ERROR" || status === "BAD_REQUEST";
              if (errorDetail) {
                upsertRecentScan({ id: scanId, status: "error", message: errorDetail.errorMessage || errorDetail.errorDetails || "Not in-scanned" });
              } else if (overallFailed) {
                const first = errors[0] || {};
                upsertRecentScan({ id: scanId, status: "error", message: first.errorMessage || first.errorDetails || "In-scan failed" });
              } else {
                upsertRecentScan({ id: scanId, status: "success", message: "Success" });
              }
              fetchTodayScans();   // refresh the server-backed "Today's Scans (all)" list
              resolve();
              return;
            }

            if (attempt < maxAttempts) {
              setTimeout(() => pollJobStatus(attempt + 1), delayMs);
            } else {
              upsertRecentScan({ id: scanId, status: "error", message: "Timed out waiting for result" });
              resolve();
            }
          } catch (err) {
            upsertRecentScan({ id: scanId, status: "error", message: "Error checking status" });
            resolve();
          }
        };

        pollJobStatus();
      })
      .catch((error) => {
        upsertRecentScan({ id: scanId, status: "error", message: error?.response?.data?.message || error.message || "Request failed" });
        resolve();
      });
  });

  const buttonsyle = {
    background: "#00a6ed",
    color: "#fff",
    padding: "3px",
    marginRight: "10px",
    borderColor: "#00a6ed",
    cursor: "pointer",
    marginTop: "3%",
  };

  const [successMessages, setSuccessMessages] = useState([]);
  const [errorMessages, setErrorMessages] = useState([]);

  useEffect(() => {
    // Fetch drivers from the API on component mount
    // fetchDrivers();
    getHomebase();
  }, []);

  const [scaninfo, setScaninfo] = useState({
    home_base: "",
  });

  const [homebases, sethomebases] = useState([]);
  const getHomebase = async () => {
    const API_PATH = `${API_URL}shypvdriverapp/homebase/getHomebases`;

    try {
      const res = await axios.get(API_PATH);
      console.log(res.data);
      if (res.data.success == true) {
        sethomebases(res.data.data);
      } else {
        console.log("false");
      }
    } catch (err) {
      console.log(err);
    }
  };

  console.log("homebases", homebases);

  console.log("scan:", scaninfo);
  const [msg, setMsg] = useState();
  const [errorMsg, setError] = useState();

  function doInScan() {
    if (scaninfo.scan_date != "" && scaninfo.home_base && scaninfo.scan_time) {
      setError("");
      setMsg("");
      setProjectMsg("");
      const payload = {
        scan_date: scaninfo.scan_date,
        driverId: scaninfo.driver,
        scan_time: scaninfo.scan_time,
        home_base: scaninfo.home_base,
        status: "inscan",
        updated_by: sessionStorage.getItem("userid"),
        awbs: shipmentDetails,
        service_mode: serviceMode,
      };

      console.log(payload);
      setMsg("processing...");
      axios
        .post(`${API_URL}shypvdriverapp/jobs/inScanShipmentsNew`, payload)
        .then((response) => {
          console.log("Response:", response.data);

          if (response.data.data) {
            const jobId = response.data.data.requestId;
            console.log("Job Started. Job ID:", jobId);
            // alert(jobId)

            // Optional: link the scanned AWBs to a finance Project.
            // Best-effort — never block or fail the in-scan itself.
            if (projectId) {
              axios
                .post(`${API_URL}shypvdriverapp/projects/${projectId}/assign`, {
                  awbs: shipmentDetails,
                })
                .then((r) => {
                  const res = r.data && r.data.data;
                  if (!res) return;
                  const mismatch = res.partnerMismatch || [];
                  let note = `${res.matched || 0} AWB(s) linked to project`;
                  if (mismatch.length > 0) {
                    note += ` · ${mismatch.length} skipped — partner mismatch (${mismatch.join(", ")})`;
                  }
                  setProjectMsg(note);
                })
                .catch((e) =>
                  console.warn("Project link failed (in-scan still ok):", e?.message)
                );
            }

            // Step 2: Poll the job status until it is NOT "PENDING"
            const maxAttempts = 30; // Maximum number of polling attempts
            const delayMs = 4000; // Delay between each poll (2 seconds)

            const pollJobStatus = async (attempt = 1) => {
              try {
                const statusResponse = await axios.get(
                  `${API_URL}shypvdriverapp/jobs/inScanStatus/${jobId}`
                );
                const status = statusResponse.data.STATUS;

                console.log(`Attempt ${attempt}: Job status is ${status}`);

                if (status !== "PENDING" && status !== "QUEUED") {
                  console.log("✅ Job is complete:", statusResponse.data);

                  const awbs = statusResponse.data.data.awbs.split(",");
                  const errors = statusResponse.data.data.errors || []; // Default to an empty array if no errors

                  const successList = [];
                  const errorList = [];

                  awbs.forEach((awb) => {
                    const errorDetail = errors.find((error) =>
                      error.errorDetails.includes(awb)
                    );

                    if (errorDetail) {
                      // If an error exists for this AWB, add it to the error list
                      errorList.push(`${awb}: ${errorDetail.errorMessage}`);
                    } else {
                      // If no error is found, it's a success
                      successList.push(`${awb}: Success`);
                    }
                  });

                  // Update the state correctly
                  setSuccessMessages(successList);
                  setErrorMessages(errorList);

                  // Build the final message
                  let msg = "";
                  if (successList.length > 0) {
                    msg += `Success:\n${successList.join("\n")}\n`;
                  }
                  if (errorList.length > 0) {
                    if (msg) msg += "\n";
                    msg += `Errors:\n${errorList.join("\n")}`;
                  }
                  if (!msg) {
                    msg = "Processing.";
                  }

                  setMsg(msg);
                  setLoading(false);
                  return;
                }

                if (attempt < maxAttempts) {
                  setTimeout(() => pollJobStatus(attempt + 1), delayMs);
                } else {
                  console.warn("Max retries reached. Job is still pending.");
                  setError("Timeout: Job did not complete in time.");
                  setLoading(false);
                }
              } catch (err) {
                console.error(
                  "Polling error:",
                  err.response?.data || err.message
                );
                setError("Error checking job status.");
                setLoading(false);
              }
            };

            pollJobStatus();
          }

          if (response.data.errors && response.data.errors.length > 0) {
            response.data.errors.forEach((err) => {
              if (Array.isArray(err.errorDetails)) {
                err.errorDetails.forEach((detail) => {
                  errorMessages.push(` ${detail}`);
                });
              } else {
                errorMessages.push(`${err.errorDetails}`);
              }
            });
          }

          // Build the message with line breaks
          let msg = "";
          if (successMessages.length > 0) {
            msg += `Success:\n${successMessages.join("\n")}\n`;
          }
          if (errorMessages.length > 0) {
            if (msg) msg += "\n"; // add spacing if successes exist
            msg += `Errors:\n${errorMessages.join("\n")}`;
          }
          if (!msg) {
            msg = "Processing.";
          }

          // Set the message
          setMsg(msg);
        })
        .catch((error) => {
          console.error("Error:", error);
        });
    } else {
      setError("Please enter all the required fields");
    }
  }

  const handleDelete = (awbToDelete) => {
    // Filter out the AWB to delete
    const updatedDetails = shipmentDetails.filter((awb) => awb !== awbToDelete);
    setShipmentDetails(updatedDetails);
  };
  return (
    <>
      <div className="w-card">
        <div className="w-card-header">
          <span className="w-card-title"><i className="fa fa-calendar" aria-hidden="true" /> Scan details</span>
        </div>
        <div className="w-card-body">
          {/* Service-Time: live clock, editable cutoff, current auto mode, station override */}
          <div className="w-st-banner">
            <div className="w-st-clock">
              <i className="fa fa-clock-o" aria-hidden="true" />
              <span className="w-st-time">{clockInZone(now)}</span>
              <span className="w-st-tz">Dubai time</span>
            </div>

            <div className="w-st-cell">
              <span className="w-st-label">Cutoff</span>
              {cutoffEditing ? (
                <span className="w-st-edit">
                  <input
                    type="time"
                    className="w-input"
                    style={{ width: 130 }}
                    value={cutoffDraft}
                    onChange={(e) => setCutoffDraft(e.target.value)}
                  />
                  <button type="button" className="w-btn w-btn-primary" disabled={cutoffSaving} onClick={saveCutoff}>
                    {cutoffSaving ? "Saving…" : "Save"}
                  </button>
                  <button type="button" className="w-btn" onClick={() => setCutoffEditing(false)}>Cancel</button>
                </span>
              ) : (
                <span className="w-st-edit">
                  <strong>{cutoffHHmm}</strong>
                  <button
                    type="button"
                    className="w-st-editbtn"
                    onClick={() => { setCutoffDraft(cutoffHHmm); setCutoffEditing(true); }}
                  >
                    <i className="fa fa-pencil" aria-hidden="true" /> Edit
                  </button>
                </span>
              )}
            </div>

            <div className="w-st-cell">
              <span className="w-st-label">Auto now</span>
              <Pill tone={computeAutoLabel(now) === "Same Day" ? "success" : "warning"}>{computeAutoLabel(now)}</Pill>
            </div>

            <div className="w-st-cell">
              <span className="w-st-label">Station mode</span>
              <select
                className="w-select"
                style={{ minWidth: 160 }}
                value={serviceMode}
                onChange={(e) => setServiceMode(e.target.value)}
              >
                <option value="AUTO">Auto (by time)</option>
                <option value="SAME_DAY">Force Same Day</option>
                <option value="NEXT_DAY">Force Next Day</option>
              </select>
            </div>

            {serviceMode !== "AUTO" && (
              <div className="w-st-note">
                <i className="fa fa-exclamation-triangle" aria-hidden="true" /> Stamping every scan as{" "}
                <strong>{labelForMode(serviceMode)}</strong> until you refresh.
              </div>
            )}
          </div>

          <div className="w-toggle-row" style={{ marginBottom: 16 }}>
            <label className="w-toggle">
              <input type="checkbox" checked={continuousMode} onChange={handleToggleContinuous} />
              <span className="w-toggle-slider" />
            </label>
            <span className="w-toggle-label">Continuous Scanning</span>
            <span className="w-help-text">
              {continuousMode
                ? "— only Homebase is required; each scan submits immediately and logs below."
                : "— scan a batch, then submit once."}
            </span>
          </div>

          <div className="w-form-row">
            {/* Date Input */}
            {!continuousMode && (
              <div className="w-field" style={{ flex: "1 1 160px" }}>
                <label className="w-field-label" htmlFor="date">Date</label>
                <input
                  type="date"
                  id="date"
                  className="w-input"
                  onChange={(e) =>
                    setScaninfo({ ...scaninfo, scan_date: e.target.value })
                  }
                  value={scaninfo.scan_date}
                />
              </div>
            )}

            {/* Time Input */}
            {!continuousMode && (
              <div className="w-field" style={{ flex: "1 1 160px" }}>
                <label className="w-field-label" htmlFor="time">Time</label>
                <input
                  type="time"
                  id="time"
                  className="w-input"
                  onChange={(e) =>
                    setScaninfo({ ...scaninfo, scan_time: e.target.value })
                  }
                  value={scaninfo.scan_time}
                />
              </div>
            )}

            {/* Driver Selection */}
            {/* <div className="form-group">
                    <label htmlFor="driver">Select Driver:</label>
                    <select
                        id="driver"
                        value={scaninfo.driver}
                        onChange={(e) => setScaninfo({ ...scaninfo, driver: e.target.value })}

                    >
                        <option value="">Select a driver</option>
                        {drivers&& drivers.map((driver) => (
                            <option key={driver.driver_id} value={driver.driver_id}>
                                {driver.driver_name}
                            </option>
                        ))}
                    </select>
                </div> */}

            <div className="w-field" style={{ flex: "1 1 220px" }}>
              <label className="w-field-label" htmlFor="homebase">Select Homebase</label>
              <select
                id="homebase"
                className="w-select"
                style={{ width: "100%" }}
                value={scaninfo.home_base}
                onChange={(e) =>
                  setScaninfo({ ...scaninfo, home_base: e.target.value })
                }
              >
                <option value="">Select a Homebase</option>
                {homebases &&
                  homebases.map((homebase) => (
                    <option
                      key={homebase.homebaseCode}
                      value={homebase.homebaseCode}
                    >
                      {homebase.homebaseName}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Barcode Input */}
          <div className="w-field" style={{ marginBottom: 0 }}>
            <label className="w-field-label" htmlFor="awb-input">AWB</label>
            {/* <input
                        type="text"
                        value={barcode}
                        onChange={handleBarcodeInputChange}
                        onKeyUp={handleBarcodeKeyUp}
                        placeholder="Scan barcode and press Enter"
                        autoFocus
                    /> */}

            <textarea
              id="awb-input"
              className="w-textarea"
              value={barcode}
              onChange={handleBarcodeInputChange}
              // onKeyUp={handleBarcodeKeyUp}
              onKeyDown={handleBarcodeKeyDown}
              placeholder={continuousMode ? "Scan barcode — submits automatically" : "Scan barcode and press Enter"}
              autoFocus
            />
          </div>
        </div>
      </div>

      {continuousMode ? (
        <>
        <div className="w-card">
          <div className="w-card-header">
            <span className="w-card-title"><i className="fa fa-history" aria-hidden="true" /> Recent Scans (this session)</span>
            {batchProgress
              ? <span className="w-card-meta"><i className="fa fa-spinner fa-spin" aria-hidden="true" /> Processing {batchProgress.done}/{batchProgress.total} (batches of {BATCH_SIZE})…</span>
              : (recentScans.length > 0 && <span className="w-card-meta">{recentScans.length}</span>)}
          </div>
          <div className="w-card-body">
            {errorMsg && <div className="w-pill w-pill-danger" style={{ marginBottom: 12 }}>{errorMsg}</div>}
            {recentScans.length === 0 ? (
              <div className="w-help-text">Scanned AWBs will appear here as they're submitted.</div>
            ) : (
              <div className="w-scan-list">
                {recentScans.map((entry) => (
                  <div key={entry.id} className="w-scan-row">
                    <span className="w-scan-awb">{entry.awb}</span>
                    <span className="w-scan-time">{entry.time}</span>
                    {entry.serviceTime && (
                      <Pill tone={entry.serviceTime === "Same Day" ? "success" : "warning"}>{entry.serviceTime}</Pill>
                    )}
                    <Pill tone={entry.status === "success" ? "success" : entry.status === "error" ? "danger" : "warning"}>
                      {entry.status === "pending" ? "Pending" : entry.status === "success" ? "Success" : "Error"}
                    </Pill>
                    {entry.projectNote && (
                      <Pill tone={entry.projectNote === "mismatch" ? "danger" : "primary"}>
                        {entry.projectNote === "mismatch" ? "Project: partner mismatch" : "Project linked"}
                      </Pill>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="w-card">
          <div className="w-card-header">
            <span className="w-card-title"><i className="fa fa-list-ul" aria-hidden="true" /> Today's Scans (all)</span>
            <span className="w-card-meta" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {todayData ? `${todayData.distinctAwbs || 0} AWBs · ${todayData.totalScans || 0} scans` : ""}
              <button type="button" className="btn btn-link p-0" style={{ fontSize: 12 }} onClick={fetchTodayScans} disabled={todayLoading}>
                {todayLoading ? "…" : "Refresh"}
              </button>
            </span>
          </div>
          <div className="w-card-body">
            <input
              placeholder="Search AWB…"
              value={todaySearch}
              onChange={(e) => setTodaySearch(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", marginBottom: 10, borderRadius: 8, border: "1px solid #d8dee9", boxSizing: "border-box" }}
            />
            {todayAwbsFiltered.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 320, overflowY: "auto" }}>
                {todayAwbsFiltered.map((awb) => (
                  <span key={awb} className="w-id-chip" style={{ fontFamily: "monospace" }}>{awb}</span>
                ))}
              </div>
            ) : (
              <div className="w-help-text">{todayLoading ? "Loading…" : (todaySearch ? "No match." : "Nothing scanned today yet.")}</div>
            )}
          </div>
        </div>
        </>
      ) : (
      <div className="w-card">
        <div className="w-card-header">
          <span className="w-card-title"><i className="fa fa-list-alt" aria-hidden="true" /> Shipment Details</span>
          <span className="w-card-meta" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {shipmentDetails.length > 0 && <span>{shipmentDetails.length} scanned</span>}
            <Pill tone={currentServiceLabel() === "Same Day" ? "success" : "warning"}>{currentServiceLabel()}</Pill>
          </span>
        </div>
        <div className="w-card-body">
          {loading && (
            <div className="w-loading"><i className="fa fa-spinner fa-spin" aria-hidden="true" /> Loading shipment details...</div>
          )}

          {shipmentDetails.length > 0 && (
            <div>
              <div className="w-scan-list">
                {shipmentDetails.map((awb, index) => (
                  //         <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  //   <p style={{ textAlign: 'left', marginRight: '10px', marginBottom: '0' }}>{awb}</p>
                  //   <a
                  //     style={{ color: 'red', cursor: 'pointer', marginLeft: '10px' }}
                  //     onClick={() => handleDelete(awb)} // Delete this AWB when clicked
                  //   >
                  //     <i className="fa fa-trash" />
                  //   </a>
                  // </div>

                  <div key={index} className="w-scan-row">
                    <span className="w-scan-awb">{awb}</span>
                    <button
                      type="button"
                      className="w-scan-remove"
                      onClick={() => handleDelete(awb)} // Delete this AWB when clicked
                      aria-label="Remove"
                    >
                      <i className="fa fa-trash" aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>

              <button
                onClick={doInScan}
                type="button"
                className="w-btn w-btn-primary"
                style={{ marginTop: 14 }}
              >
                Submit
              </button>
              {msg && <pre className="w-result-pre">{msg}</pre>}
              {projectMsg && (
                <div
                  className={`w-pill ${projectMsg.includes("mismatch") ? "w-pill-warning" : "w-pill-primary"}`}
                  style={{ marginTop: 12 }}
                >
                  {projectMsg}
                </div>
              )}
            </div>
          )}

          {errorMsg && <div className="w-pill w-pill-danger" style={{ marginTop: 12 }}>{errorMsg}</div>}
        </div>
      </div>
      )}
    </>
  );
};

export default BarcodeScanner;
